import { useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import { ChevronRight, ChevronDown } from "lucide-react";

const PLANET_COLORS = {
  Sun: "text-orange-400", Moon: "text-slate-200", Mars: "text-red-400",
  Mercury: "text-emerald-400", Jupiter: "text-yellow-400", Venus: "text-pink-400",
  Saturn: "text-blue-400", Rahu: "text-slate-400", Ketu: "text-amber-600",
};

const PLANET_BG = {
  Sun: "bg-orange-500/8", Moon: "bg-slate-400/8", Mars: "bg-red-500/8",
  Mercury: "bg-emerald-500/8", Jupiter: "bg-yellow-500/8", Venus: "bg-pink-500/8",
  Saturn: "bg-blue-500/8", Rahu: "bg-slate-500/8", Ketu: "bg-amber-600/8",
};

function isCurrent(startDate, endDate) {
  const now = new Date();
  return new Date(startDate) <= now && now <= new Date(endDate);
}

export default function DashaPeriods({ dashas }) {
  const [expanded, setExpanded] = useState(null);

  if (!dashas || dashas.length === 0) return null;

  const toggle = (i) => setExpanded(expanded === i ? null : i);

  return (
    <div data-testid="dasha-periods">
      <p className="text-xs text-slate-500 mb-3">
        Vimshottari Maha Dasha &amp; Antar Dasha. Click a Maha Dasha to see sub-periods.
      </p>
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-amber-400 font-cinzel text-xs tracking-wider w-8"></TableHead>
            <TableHead className="text-amber-400 font-cinzel text-xs tracking-wider">Dasha</TableHead>
            <TableHead className="text-amber-400 font-cinzel text-xs tracking-wider">Start</TableHead>
            <TableHead className="text-amber-400 font-cinzel text-xs tracking-wider">End</TableHead>
            <TableHead className="text-amber-400 font-cinzel text-xs tracking-wider">Years</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dashas.map((d, i) => {
            const active = isCurrent(d.start_date, d.end_date);
            const isOpen = expanded === i;
            const hasAntar = d.antar_dashas && d.antar_dashas.length > 0;

            return (
              <MahaDashaBlock
                key={i}
                dasha={d}
                index={i}
                active={active}
                isOpen={isOpen}
                hasAntar={hasAntar}
                onToggle={() => toggle(i)}
              />
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function MahaDashaBlock({ dasha, index, active, isOpen, hasAntar, onToggle }) {
  const d = dasha;
  return (
    <>
      {/* Maha Dasha row */}
      <TableRow
        className={`border-white/5 transition-colors cursor-pointer select-none ${
          active ? "bg-amber-500/10 border-l-2 border-l-amber-500" : "hover:bg-white/5"
        } ${isOpen ? "bg-white/[0.03]" : ""}`}
        onClick={onToggle}
        data-testid={`dasha-row-${d.planet.toLowerCase()}`}
      >
        <TableCell className="pr-0 w-8">
          {hasAntar && (
            isOpen
              ? <ChevronDown className="h-3.5 w-3.5 text-amber-400" />
              : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
          )}
        </TableCell>
        <TableCell>
          <span className={`font-semibold text-sm ${PLANET_COLORS[d.planet] || "text-slate-200"}`}>
            {d.planet}
          </span>
          {active && (
            <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
              ACTIVE
            </span>
          )}
        </TableCell>
        <TableCell className="font-data text-xs text-slate-300">{d.start_date}</TableCell>
        <TableCell className="font-data text-xs text-slate-300">{d.end_date}</TableCell>
        <TableCell className="font-data text-sm text-slate-200">{d.duration_years}</TableCell>
      </TableRow>

      {/* Antar Dasha rows */}
      {isOpen && hasAntar && d.antar_dashas.map((ad, j) => {
        const adActive = isCurrent(ad.start_date, ad.end_date);
        return (
          <TableRow
            key={`ad-${index}-${j}`}
            className={`border-white/[0.03] transition-colors ${
              adActive ? "bg-violet-500/10 border-l-2 border-l-violet-400" : `${PLANET_BG[ad.planet] || ""}`
            }`}
            data-testid={`antar-dasha-${d.planet.toLowerCase()}-${ad.planet.toLowerCase()}`}
          >
            <TableCell className="pr-0 w-8"></TableCell>
            <TableCell className="pl-6">
              <span className="text-xs text-slate-500 mr-1.5">{d.planet}-</span>
              <span className={`text-xs font-medium ${PLANET_COLORS[ad.planet] || "text-slate-300"}`}>
                {ad.planet}
              </span>
              {adActive && (
                <span className="ml-1.5 text-[9px] bg-violet-500/20 text-violet-300 px-1 py-0.5 rounded-full">
                  NOW
                </span>
              )}
            </TableCell>
            <TableCell className="font-data text-[11px] text-slate-400">{ad.start_date}</TableCell>
            <TableCell className="font-data text-[11px] text-slate-400">{ad.end_date}</TableCell>
            <TableCell className="font-data text-[11px] text-slate-400">{ad.duration_years}y</TableCell>
          </TableRow>
        );
      })}
    </>
  );
}
